import axios from "axios";

import { env } from "@/env";
import { CookiesKeys, CookiesService } from "@/utils";

import { refreshAccessToken } from "./auth";

const isServer = typeof window === "undefined";

export const instance = axios.create({
  baseURL: `${env.NEXT_PUBLIC_BACKEND_API}/v1/`,
  timeout: 50000,
});

instance.interceptors.request.use(
  async (config) => {
    let accessToken;

    if (isServer) {
      const { cookies } = await import("next/headers");
      accessToken = cookies().get(CookiesKeys.AccessToken)?.value;
    } else {
      accessToken = CookiesService.getAccessToken();
    }

    if (accessToken) {
      config.headers["Authorization"] = `Bearer ${accessToken}`;
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

instance.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const originalRequest = error.config;

    const { message, statusCode } = error.response.data;

    if ((message === "invalid token" || message === "invalid signature") && statusCode === 401) {
      window.location.href = "/auth/login";
      CookiesService.deleteAuthTokens();

      return Promise.reject();
    }

    if (message === "jwt expired" && statusCode === 401) {
      originalRequest._retry = true;

      const refreshToken = CookiesService.getRefreshToken();
      if (refreshToken) {
        try {
          const response = await refreshAccessToken({ refreshToken });

          const newAccessToken = response.accessToken;
          const newRefreshToken = response.refreshToken;
          CookiesService.setAccessToken(newAccessToken);
          CookiesService.setRefreshToken(newRefreshToken);
          originalRequest.headers["Authorization"] = `Bearer ${newAccessToken}`;

          return axios(originalRequest);
        } catch (refreshError) {
          CookiesService.deleteAuthTokens();

          console.error("Token refresh failed", refreshError);
          window.location.href = "/auth/login";

          return Promise.reject(refreshError);
        }
      }
    }

    return Promise.reject(error);
  }
);

export { instance as axios };
